import { useQuery } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { api } from '@/lib/api';

/** Checks that are informational when "off" — not security failures. */
const ADVISORY_CHECKS = new Set(['cookie_sessions']);

function checkLooksOk(c) {
  if (c.ok) return true;
  if (ADVISORY_CHECKS.has(c.id)) return true;
  return false;
}

export default function SuperSecurityPage() {
  const q = useQuery({
    queryKey: ['super-security'],
    queryFn: async () => {
      const r = await api('/api/v1/super/security-status');
      if (r.status !== 200) throw new Error('security');
      return r.data;
    },
  });

  const health = useQuery({
    queryKey: ['super-health'],
    queryFn: async () => {
      const r = await api('/api/v1/super/health-detail');
      if (r.status !== 200) throw new Error('health');
      return r.data;
    },
  });

  const checks = q.data?.checks ?? [];
  const failed = checks.filter((c) => !checkLooksOk(c)).length;
  const okCount = checks.filter((c) => checkLooksOk(c)).length;

  return (
    <div className="space-y-6 motion-enter">
      <ModuleHeader
        title="Security status"
        description="Hardening checklist for the live API. App-level shields — not a replacement for Cloud Armor / WAF."
        icon="Shield"
        metrics={[
          { label: 'Checks OK', value: okCount },
          { label: 'Attention', value: failed },
          { label: 'Uptime s', value: q.data?.uptimeSec ?? '—' },
          { label: 'Store', value: q.data?.storeMode ?? '—' },
        ]}
      />

      <Card>
        <CardContent className="pt-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <th className="pb-2">Check</th>
                <th className="pb-2">Status</th>
                <th className="pb-2">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {checks.map((c) => {
                const ok = checkLooksOk(c);
                return (
                  <tr key={c.id}>
                    <td className="py-2 font-mono text-xs">{c.id}</td>
                    <td className="py-2">
                      <StatusPill status={ok ? 'ready' : 'error'} />
                    </td>
                    <td className="py-2">
                      {c.detail}
                      {ADVISORY_CHECKS.has(c.id) && !c.ok ? (
                        <span className="ml-2 text-xs text-muted-foreground">(expected default)</span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
              {!checks.length && (
                <tr>
                  <td colSpan={3} className="py-4 text-muted-foreground">
                    {q.isLoading ? 'Loading…' : 'No checks returned'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Health detail</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="font-mono text-xs whitespace-pre-wrap overflow-x-auto rounded-md bg-muted/40 p-4">
            {JSON.stringify(health.data ?? {}, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
